import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonList,
  IonPopover,
  IonToast,
  IonActionSheet,
} from '@ionic/react';
import {
  copyOutline,
  chevronDownOutline,
  personOutline,
  eyeOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';
import type { DraftMode, MessageDraft } from '../lib/localStore';
import { deleteDraft, getDraft, upsertDraft } from '../lib/localStore';
import { buildJoinUrl, buildShareText, recipientFromFields } from '../lib/draftShare';
import { generateMessageDek } from '../lib/chatCrypto';
import ChatComposerBar, { type ComposerMode } from './ChatComposerBar';

export interface QuickComposeRootProps {
  variant: 'anonymous' | 'authenticated';
  draftId?: string;
  onSaved?: () => void;
  onSend?: (draft: MessageDraft) => Promise<void>;
  children: ReactNode;
}

interface QuickComposeContextValue {
  variant: 'anonymous' | 'authenticated';
  body: string;
  mode: DraftMode;
  encodeKey: string;
  recipientName: string;
  recipientEmail: string;
  recipientPhone: string;
  sending: boolean;
  persistDraft: (silent?: boolean) => void;
  handleShare: () => void;
  handleSend: () => void;
  handleClear: () => void;
  handleComposerSubmit: () => void;
  setBody: (v: string) => void;
  setMode: (m: ComposerMode) => void;
  setRecipientName: (v: string) => void;
  setRecipientEmail: (v: string) => void;
  setRecipientPhone: (v: string) => void;
  toast: string | null;
  setToast: (v: string | null) => void;
  showShareSheet: boolean;
  setShowShareSheet: (v: boolean) => void;
  shareDraft: MessageDraft;
}

const QuickComposeContext = createContext<QuickComposeContextValue | null>(null);

export function useQuickComposeContext(): QuickComposeContextValue {
  const ctx = useContext(QuickComposeContext);
  if (!ctx) throw new Error('QuickCompose components must be used within QuickComposeRoot');
  return ctx;
}

function newDraftId(): string {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function QuickComposeRoot({
  variant,
  draftId: initialDraftId,
  onSaved,
  onSend,
  children,
}: QuickComposeRootProps) {
  const { t } = useI18n();
  const [draftId, setDraftId] = useState(initialDraftId ?? newDraftId());
  const [body, setBody] = useState('');
  const [mode, setMode] = useState<DraftMode>('plain');
  const [encodeKey, setEncodeKey] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [sending, setSending] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleModeChange = useCallback((next: ComposerMode) => {
    setMode(next);
    if (next === 'coded') {
      setEncodeKey((prev) => prev.trim() || generateMessageDek());
    }
  }, []);

  useEffect(() => {
    if (!initialDraftId) return;
    const existing = getDraft(initialDraftId);
    if (!existing) return;
    setDraftId(existing.id);
    setBody(existing.body);
    setMode(existing.mode);
    setEncodeKey(
      existing.mode === 'coded'
        ? existing.encodeKey?.trim() || generateMessageDek()
        : existing.encodeKey ?? ''
    );
    setRecipientName(existing.recipient?.name ?? '');
    setRecipientEmail(existing.recipient?.email ?? '');
    setRecipientPhone(existing.recipient?.phone ?? '');
  }, [initialDraftId]);

  // Keep a DEK ready whenever coded mode is active (no manual key UI).
  useEffect(() => {
    if (mode !== 'coded') return;
    if (encodeKey.trim()) return;
    setEncodeKey(generateMessageDek());
  }, [mode, encodeKey]);

  const buildDraft = useCallback((): MessageDraft => {
    const existing = getDraft(draftId);
    const now = new Date().toISOString();
    const recipient = recipientFromFields(recipientName, recipientEmail, recipientPhone);
    return {
      id: draftId,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      body: body.trim(),
      mode,
      encodeKey:
        mode === 'coded'
          ? encodeKey.trim() || generateMessageDek()
          : undefined,
      recipient,
      status: body.trim() ? 'ready_to_send' : 'draft',
      cloudConversationId: existing?.cloudConversationId,
    };
  }, [body, draftId, encodeKey, mode, recipientEmail, recipientName, recipientPhone]);

  const shareDraft = useMemo(() => buildDraft(), [buildDraft]);

  const persistDraft = useCallback(
    (silent = false) => {
      if (!body.trim() && !recipientEmail.trim() && !recipientPhone.trim()) return;
      upsertDraft(buildDraft());
      if (!silent) {
        setToast(t('quickCompose.saved'));
        onSaved?.();
      }
    },
    [body, buildDraft, onSaved, recipientEmail, recipientPhone, t]
  );

  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (!body.trim()) return;
    saveTimer.current = setTimeout(() => persistDraft(true), 800);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [body, mode, encodeKey, recipientName, recipientEmail, recipientPhone, persistDraft]);

  const handleShare = async () => {
    if (!body.trim()) {
      setToast(t('quickCompose.messageRequired'));
      return;
    }
    persistDraft(true);
    const joinUrl = buildJoinUrl(shareDraft);
    const text = buildShareText(shareDraft, joinUrl);
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: t('quickCompose.shareTitle'), text, url: joinUrl });
        return;
      } catch {
        // fall through
      }
    }
    setShowShareSheet(true);
  };

  const handleSend = async () => {
    if (!body.trim()) {
      setToast(t('quickCompose.messageRequired'));
      return;
    }
    if (variant === 'authenticated' && !recipientEmail.trim() && !recipientPhone.trim()) {
      setToast(t('quickCompose.recipientRequired'));
      return;
    }
    if (!onSend) return;
    const draft = buildDraft();
    upsertDraft({ ...draft, status: 'ready_to_send' });
    try {
      setSending(true);
      await onSend(draft);
      setToast(t('quickCompose.sent'));
      setBody('');
      setEncodeKey('');
      setRecipientName('');
      setRecipientEmail('');
      setRecipientPhone('');
      setDraftId(newDraftId());
      onSaved?.();
    } catch (err) {
      setToast(err instanceof Error ? err.message : t('quickCompose.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleComposerSubmit = () => {
    if (variant === 'anonymous') {
      if (!body.trim()) return;
      persistDraft(true);
      setToast(t('quickCompose.signUpToSendHint'));
      return;
    }
    void handleSend();
  };

  const handleClear = () => {
    deleteDraft(draftId);
    setBody('');
    setEncodeKey('');
    setRecipientName('');
    setRecipientEmail('');
    setRecipientPhone('');
    setDraftId(newDraftId());
    onSaved?.();
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setToast(label);
    } catch {
      setToast(t('quickCompose.copyFailed'));
    }
  };

  const value: QuickComposeContextValue = {
    variant,
    body,
    mode,
    encodeKey,
    recipientName,
    recipientEmail,
    recipientPhone,
    sending,
    persistDraft,
    handleShare,
    handleSend,
    handleClear,
    handleComposerSubmit,
    setBody,
    setMode: handleModeChange,
    setRecipientName,
    setRecipientEmail,
    setRecipientPhone,
    toast,
    setToast,
    showShareSheet,
    setShowShareSheet,
    shareDraft,
  };

  return (
    <QuickComposeContext.Provider value={value}>
      {children}
      <IonToast
        isOpen={!!toast}
        message={toast ?? ''}
        duration={2500}
        onDidDismiss={() => setToast(null)}
      />
      <IonActionSheet
        isOpen={showShareSheet}
        onDidDismiss={() => setShowShareSheet(false)}
        header={t('quickCompose.shareSheetTitle')}
        buttons={[
          {
            text: t('quickCompose.copyLink'),
            icon: copyOutline,
            handler: () => {
              void copyToClipboard(buildJoinUrl(shareDraft), t('quickCompose.linkCopied'));
            },
          },
          {
            text: t('quickCompose.copyMessage'),
            icon: copyOutline,
            handler: () => {
              void copyToClipboard(
                buildShareText(shareDraft, buildJoinUrl(shareDraft)),
                t('quickCompose.messageCopied')
              );
            },
          },
          { text: t('common.actions.cancel'), role: 'cancel' },
        ]}
      />
    </QuickComposeContext.Provider>
  );
}

export function QuickComposeGuestStrip() {
  const { t } = useI18n();
  const history = useHistory();
  const { variant } = useQuickComposeContext();

  if (variant !== 'anonymous') return null;

  return (
    <div className="quick-guest-strip" slot="fixed">
      <IonIcon icon={eyeOutline} className="quick-guest-strip__icon" aria-hidden="true" />
      <span className="quick-guest-strip__text">{t('quickCompose.guestStrip')}</span>
      <button
        type="button"
        className="quick-guest-strip__link"
        onClick={() => history.push('/sign-up')}
      >
        {t('quickCompose.signUpLink')}
      </button>
    </div>
  );
}

function recipientInitials(name: string, email: string, phone: string): string {
  const trimmedName = name.trim();
  if (trimmedName) {
    const parts = trimmedName.split(/\s+/).filter(Boolean);
    return (parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '');
  }
  const trimmedEmail = email.trim();
  if (trimmedEmail) return trimmedEmail.charAt(0).toUpperCase();
  const digits = phone.replace(/\D/g, '');
  if (digits.length > 0) return digits.slice(-1);
  return '';
}

export function QuickComposeHeaderTitle({
  onPress,
}: {
  onPress: () => void;
}) {
  const { t } = useI18n();
  const { recipientName, recipientEmail, recipientPhone } = useQuickComposeContext();

  const hasRecipient = Boolean(
    recipientName.trim() || recipientEmail.trim() || recipientPhone.trim()
  );

  const primaryLabel = hasRecipient
    ? recipientName.trim() || recipientEmail.trim() || recipientPhone.trim()
    : t('quickCompose.addRecipient');

  const secondaryLabel =
    recipientName.trim() && recipientEmail.trim()
      ? recipientEmail.trim()
      : recipientName.trim() && recipientPhone.trim()
        ? recipientPhone.trim()
        : !recipientName.trim() && recipientEmail.trim() && recipientPhone.trim()
          ? recipientPhone.trim()
          : null;

  const initials = recipientInitials(recipientName, recipientEmail, recipientPhone);

  return (
    <button
      type="button"
      id="quick-recipient-trigger"
      className={`quick-compose-header-title${hasRecipient ? '' : ' quick-compose-header-title--empty'}`}
      onClick={onPress}
      aria-label={t('quickCompose.recipientPopoverTitle')}
    >
      <span className="quick-compose-header-title__inner">
        <span className="quick-compose-header-title__avatar" aria-hidden="true">
          {initials ? (
            initials.toUpperCase()
          ) : (
            <IonIcon icon={personOutline} />
          )}
        </span>
        <span className="quick-compose-header-title__text">
          <span className="quick-compose-header-title__name">{primaryLabel}</span>
          {secondaryLabel && (
            <span className="quick-compose-header-title__sub">{secondaryLabel}</span>
          )}
        </span>
        <IonIcon icon={chevronDownOutline} className="quick-compose-header-title__chevron" />
      </span>
    </button>
  );
}

export function QuickComposeRecipientPopover({
  isOpen,
  onDismiss,
  triggerId,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  triggerId: string;
}) {
  const { t } = useI18n();
  const {
    recipientName,
    recipientEmail,
    recipientPhone,
    setRecipientName,
    setRecipientEmail,
    setRecipientPhone,
  } = useQuickComposeContext();

  const hasRecipient = Boolean(
    recipientName.trim() || recipientEmail.trim() || recipientPhone.trim()
  );

  return (
    <IonPopover
      className="vault-glass-popover quick-recipient-popover"
      trigger={triggerId}
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      dismissOnSelect={false}
      side="bottom"
      alignment="center"
    >
      <div className="quick-recipient-sheet">
        <div className="quick-recipient-sheet__head">
          <span className="quick-recipient-sheet__avatar" aria-hidden="true">
            {recipientInitials(recipientName, recipientEmail, recipientPhone).toUpperCase() || (
              <IonIcon icon={personOutline} />
            )}
          </span>
          <div>
            <h3>{t('quickCompose.recipientPopoverTitle')}</h3>
            <p>{t('quickCompose.recipientPopoverHint')}</p>
          </div>
        </div>

        <IonList lines="none" className="quick-recipient-sheet__fields">
          <IonItem>
            <IonInput
              type="email"
              label={t('quickCompose.recipientEmail')}
              labelPlacement="stacked"
              placeholder={t('quickCompose.recipientEmailPlaceholder')}
              value={recipientEmail}
              onIonInput={(e) => setRecipientEmail(e.detail.value ?? '')}
            />
          </IonItem>
          <IonItem>
            <IonInput
              label={t('quickCompose.recipientName')}
              labelPlacement="stacked"
              placeholder={t('quickCompose.recipientNamePlaceholder')}
              value={recipientName}
              onIonInput={(e) => setRecipientName(e.detail.value ?? '')}
            />
          </IonItem>
          <IonItem>
            <IonInput
              type="tel"
              label={t('quickCompose.recipientPhone')}
              labelPlacement="stacked"
              placeholder={t('quickCompose.recipientPhonePlaceholder')}
              value={recipientPhone}
              onIonInput={(e) => setRecipientPhone(e.detail.value ?? '')}
            />
          </IonItem>
        </IonList>

        {hasRecipient && (
          <p className="quick-recipient-sheet__preview">
            {t('quickCompose.recipientPreview', {
              name:
                recipientName.trim() ||
                recipientEmail.trim() ||
                recipientPhone.trim(),
            })}
          </p>
        )}

        <IonButton expand="block" className="quick-recipient-sheet__done" onClick={onDismiss}>
          {t('quickCompose.recipientDone')}
        </IonButton>
      </div>
    </IonPopover>
  );
}

export function QuickComposeMessageArea() {
  const { t } = useI18n();
  const { body, mode } = useQuickComposeContext();

  if (!body.trim()) {
    return null;
  }

  const isGhost = mode === 'ghost';
  const isCoded = mode === 'coded';

  return (
    <IonList className="vault-feed-list">
      <IonItem lines="none" className="message-row me">
        <div className="message-content">
          {isCoded && (
            <span className="encoded-badge">{t('chat.composer.mode.coded')}</span>
          )}
          {isGhost && (
            <span className="ghost-badge">{t('chat.composer.mode.ghost')}</span>
          )}
          <div
            className={`message-bubble${isGhost ? ' ghost-hidden-bubble' : ''}${
              isCoded ? ' encoded-transition' : ''
            }`}
          >
            <p className="message-text">{body}</p>
          </div>
          <div className="message-meta">{t('quickCompose.localDraftLabel')}</div>
        </div>
      </IonItem>
    </IonList>
  );
}

/** @deprecated Use QuickComposeGuestStrip for anonymous pages */
export function QuickComposeModeBar({ compact = false }: { compact?: boolean }) {
  if (compact) return null;
  return <QuickComposeGuestStrip />;
}

export function QuickComposeFooter({ asFooter = true }: { asFooter?: boolean }) {
  const { t } = useI18n();
  const {
    body,
    mode,
    setBody,
    setMode,
    handleComposerSubmit,
    sending,
  } = useQuickComposeContext();

  return (
    <ChatComposerBar
      asFooter={asFooter}
      value={body}
      onChange={setBody}
      mode={mode}
      onModeChange={setMode}
      onSubmit={handleComposerSubmit}
      sending={sending}
      submitAriaLabel={t('quickCompose.send')}
    />
  );
}

export interface QuickComposeWidgetProps extends Omit<QuickComposeRootProps, 'children'> {
  compact?: boolean;
}

const QuickComposeWidget: React.FC<QuickComposeWidgetProps> = (props) => (
  <QuickComposeRoot {...props}>
    <QuickComposeGuestStrip />
    <QuickComposeMessageArea />
    <QuickComposeFooter asFooter={false} />
  </QuickComposeRoot>
);

export default QuickComposeWidget;
